import React, { useEffect, useState } from "react";
import "./RecruiterModern.css";
import HomeCarousel from "../Home/HomeCarousel";
import NewsletterSection from "../Home/NewsletterSection";
import PackageCard from "./PackageCard";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SeoHead from "../../Common/SeoHead";
import { PAGE_SEO } from "../../../constants/seo";
import { 
  FaUserTie, 
  FaSearch, 
  FaChartLine, 
  FaBriefcase,
  FaCheckCircle,
  FaUsers,
  FaRocket,
  FaClock,
  FaShieldAlt,
  FaArrowRight
} from "react-icons/fa";
import { UserCount } from "../../../Redux/Slices/User";
const Recuiter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageSeo = PAGE_SEO[location.pathname] || PAGE_SEO["/recruiter"];
  const [jobCount, setJobCount] = useState(6500);
  const [employerCount, setEmployerCount] = useState(2500);
  const isAuthenticated = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { employerCount, jobcount } = await UserCount();
        setJobCount(jobcount);
        setEmployerCount(employerCount);
      } catch (error) {
        console.error("Error fetching stats", error);
      }
    };
    fetchStats();
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated && role === "Employer") {
      navigate("/employer/dashboard");
    } else if (isAuthenticated) {
      navigate("/recruiter");
    } else {
      navigate("/signup-recruiter");
    }
  };

  return (
    <>
      <SeoHead
        title={pageSeo.title}
        description={pageSeo.description}
        path={location.pathname}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: location.pathname === "/post-job" ? "Post a Job" : "For Employers", path: location.pathname },
        ]}
      />

      {/* Hero Section */}
      <section className="employer-hero">
        <div className="employer-hero-container">
          <div className="employer-hero-content">
            <div className="employer-hero-badge">
              <FaUserTie /> {location.pathname === "/post-job" ? "Post a Job" : "For Employers"}
            </div>
            <h1 className="employer-hero-title">
              {location.pathname === "/post-job" ? (
                <>
                  Post a Job and Hire Your Next <span className="employer-hero-highlight">CA Professional</span>
                </>
              ) : (
                <>
                  Let's Hire Your Next Great <span className="employer-hero-highlight">CA Professional</span>. Fast.
                </>
              )}
            </h1>
            <p className="employer-hero-subtitle">
              Connect with qualified CA, CS, and CMA professionals ready to join your team.
            </p>
            
            <div className="employer-hero-cta">
              <button onClick={handleGetStarted} className="employer-hero-btn-primary">
                <FaRocket /> Get Started Free
              </button>
              <Link to="/contact" className="employer-hero-btn-secondary">
                Talk to Sales <FaArrowRight />
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="employer-hero-stats">
              <div className="employer-hero-stat-item">
                <span className="stat-number">{jobCount}+</span>
                <span className="stat-label">Jobs Posted</span>
              </div>
              <div className="employer-hero-stat-item">
                <span className="stat-number">{employerCount}+</span>
                <span className="stat-label">Active Recruiters</span>
              </div>
              <div className="employer-hero-stat-item">
                <span className="stat-number">50K+</span>
                <span className="stat-label">Qualified Candidates</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="employer-benefits">
        <div className="employer-benefits-container">
          <h2 className="employer-section-title">
            Why Choose <span className="text-highlight">CAJobPortal</span> for Hiring?
          </h2>
          <p className="employer-section-subtitle">
            India's premier job portal exclusively for Chartered Accountants, Company Secretaries, and Cost Accountants
          </p>

          <div className="employer-benefits-grid">
            {[
              {
                icon: <FaUsers />,
                title: "Qualified Talent Pool",
                desc: "Access 50,000+ verified CA, CS, and CMA professionals actively seeking opportunities"
              },
              {
                icon: <FaRocket />,
                title: "Fast Hiring Process",
                desc: "Post jobs in minutes and start receiving applications within hours"
              },
              {
                icon: <FaSearch />,
                title: "Advanced Search",
                desc: "Filter candidates by qualification, experience, location, and specialization"
              },
              {
                icon: <FaChartLine />,
                title: "Hire with Confidence",
                desc: "Detailed candidate profiles with verified credentials and work history"
              },
              {
                icon: <FaShieldAlt />,
                title: "Trusted Platform",
                desc: "Join 2,500+ companies already hiring through CAJobPortal"
              },
              {
                icon: <FaClock />,
                title: "24/7 Support",
                desc: "Dedicated support team to help you throughout the hiring process"
              }
            ].map((benefit, index) => (
              <div key={index} className="employer-benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-desc">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="employer-how-it-works">
        <div className="employer-how-container">
          <h2 className="employer-section-title">How It Works</h2>
          <p className="employer-section-subtitle">
            Start hiring in 3 simple steps
          </p>

          <div className="employer-steps-grid">
            {[
              {
                step: "01",
                title: "Create Your Account",
                desc: "Sign up for free and complete your company profile in minutes"
              },
              {
                step: "02",
                title: "Post Your Job",
                desc: "Create detailed job postings with our easy-to-use form and reach thousands of candidates"
              },
              {
                step: "03",
                title: "Hire Top Talent",
                desc: "Review applications, shortlist candidates, and hire the perfect fit for your team"
              }
            ].map((step, index) => (
              <div key={index} className="employer-step-card">
                <div className="step-number">{step.step}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-desc">{step.desc}</p>
                {index < 2 && <div className="step-arrow"><FaArrowRight /></div>}
              </div>
            ))}
          </div>

          <div className="employer-how-cta">
            <button onClick={handleGetStarted} className="employer-cta-btn">
              Start Hiring Now <FaArrowRight />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="employer-features">
        <div className="employer-features-container">
          <div className="employer-features-content">
            <div className="features-left">
              <h2 className="employer-section-title">
                Everything You Need to <span className="text-highlight">Hire Faster</span>
              </h2>
              <ul className="features-list">
                {[
                  "Unlimited job postings on premium plans",
                  "Access to verified candidate profiles",
                  "Application tracking and management",
                  "Email and SMS notifications",
                  "Candidate database search",
                  "Dedicated account manager",
                  "Analytics and reporting dashboard",
                  "Priority customer support"
                ].map((feature, index) => (
                  <li key={index} className="feature-item">
                    <FaCheckCircle className="feature-check" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button onClick={handleGetStarted} className="features-cta-btn">
                Explore All Features <FaArrowRight />
              </button>
            </div>
            <div className="features-right">
              <div className="features-highlight-box">
                <FaBriefcase className="features-big-icon" />
                <h3>Trusted by Leading Companies</h3>
                <p>Join {employerCount}+ companies hiring CA professionals through our platform</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Logos */}
      <HomeCarousel />

      {/* Pricing Section */}
      <section className="employer-pricing">
        <div className="employer-pricing-container">
          <h2 className="employer-section-title">Choose Your Plan</h2>
          <p className="employer-section-subtitle">
            Flexible pricing plans designed for businesses of all sizes
          </p>
          <PackageCard />
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="employer-final-cta">
        <div className="employer-final-cta-container">
          <h2 className="final-cta-title">
            Ready to Find Your Next Great Hire?
          </h2>
          <p className="final-cta-subtitle">
            Join thousands of companies already using CAJobPortal to build their dream teams
          </p>
          <div className="final-cta-buttons">
            <button onClick={handleGetStarted} className="final-cta-btn-primary">
              <FaRocket /> Start Hiring Today
            </button>
            <Link to="/contact" className="final-cta-btn-secondary">
              Schedule a Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />
    </>
  );
};

export default Recuiter;
